// Shared constants between client and server

import type { ShipClassType, ShipSize } from './ships.js';

// Asteroid ore resources (lowercase kebab keys, consistent with the other
// resource families in resources.ts). Cargo ids are `resource_<key>` (e.g. resource_iron).
export const RESOURCES = {
  iron: { color: 0x8b8b8b, value: 10, name: 'Iron' },
  copper: { color: 0xb87333, value: 25, name: 'Copper' },
  titanium: { color: 0x6e8faf, value: 75, name: 'Titanium' },
  platinum: { color: 0xe5e4e2, value: 200, name: 'Platinum' },
} as const;

export type ResourceType = keyof typeof RESOURCES;

// Resource tiers - maps resource type to required minimum tool tier
// Higher tier tools can mine lower tier resources (backward compatible)
export const RESOURCE_TIERS: Record<ResourceType, number> = {
  iron: 1,      // T1 ore - any tool can mine
  copper: 2,    // T2 ore - requires T2+ tool
  titanium: 3,  // T3 ore - requires T3+ tool
  platinum: 4,  // T4 ore - requires T4 tool
};

// Asteroid sizes
export const ASTEROID_SIZES = ['small', 'medium', 'large', 'huge'] as const;
export type AsteroidSize = typeof ASTEROID_SIZES[number];

// Asteroid base HP per size. NOTE: this is the IRON (T1, mult 1.0) baseline.
// The HEALTH_MULTIPLIERS in MINING_CONFIG are applied on spawn to scale up
// higher-tier resources (Copper 1.2x, Titanium 1.5x, Platinum 2.0x). Tuned
// with the miningPower curve (12/14.4/19.2/24) so tier-matched mining of
// a small asteroid lands at ~3:00 (10s per unit, 18 units avg) — the "gold rule".
//
// HP per size scales LINEARLY with avg yield so ore/sec stays constant across
// sizes (small=medium=large=huge ≈ 6 ore/min for T1 Iron tier-matched).
// Bigger rocks = longer single-target session = more total cargo per trip —
// not a more efficient grind. Ratios:
//   small=1×, medium=2.55×, large=5.33×, huge=10.56× (matching yield avg).
export const ASTEROID_CONFIG: Record<AsteroidSize, { health: number; resources: { min: number; max: number } }> = {
  small: { health: 4320, resources: { min: 12, max: 24 } },
  medium: { health: 11040, resources: { min: 32, max: 60 } },
  large: { health: 23040, resources: { min: 72, max: 120 } },
  huge: { health: 45600, resources: { min: 140, max: 240 } },
};

// ==================== MINING CONFIGURATION ====================

export const MINING_CONFIG = {
  // Health multipliers by resource rarity (rarer = tougher)
  HEALTH_MULTIPLIERS: {
    iron: 1.0,
    copper: 1.2,
    titanium: 1.5,
    platinum: 2.0,
  } as Record<ResourceType, number>,

  // Base health by size — USED ONLY by harvestable fields (ice / gas / wreckage)
  // via getHarvestableHealth(). Asteroids have their own separate baseline in
  // ASTEROID_CONFIG.health above. Scaled ×4 alongside the asteroid rebalance
  // so per-unit timing stays consistent across all minable sources.
  BASE_HEALTH: {
    small: 7200,
    medium: 18000,
    large: 45000,
    huge: 90000,
  } as Record<AsteroidSize, number>,

  // Resource yield ranges by size (same regardless of resource type).
  // Scaled ×4 alongside BASE_HEALTH and ASTEROID_CONFIG so HP/unit stays
  // ~constant and ore/min is preserved per the gold rule.
  RESOURCE_YIELD: {
    small: { min: 12, max: 24 },
    medium: { min: 32, max: 60 },
    large: { min: 72, max: 120 },
    huge: { min: 140, max: 240 },
  } as Record<AsteroidSize, { min: number; max: number }>,

  // Maximum concurrent miners per asteroid size
  MINING_SLOTS: {
    small: 1,
    medium: 2,
    large: 3,
    huge: 4,
  } as Record<AsteroidSize, number>,

  // Mining zone calculation: radius * ZONE_MULTIPLIER + ZONE_BASE_OFFSET
  ZONE_MULTIPLIER: 1.5,
  ZONE_BASE_OFFSET: 100,

  // Mining tick configuration
  MINING_TICK_INTERVAL_MS: 500,   // How often mining damage/resources are applied

  // Explosion push when asteroid is destroyed
  EXPLOSION_PUSH_SPEED: 150,      // Velocity applied to push miners away
  EXPLOSION_PUSH_DISTANCE: 200,   // Distance to push miners from explosion center
} as const;

// ==================== HARVESTABLE FIELD CONFIGURATION ====================
// Spawn / sizing config for the V1 harvestable fields (ice, gas pocket, wreckage).
// Asteroids keep their own existing config in DynamicAsteroidZoneManager.

export const HARVESTABLE_FIELD_CONFIG = {
  ICE: {
    // Approx total ice asteroids per constellation across all zones (tier-driven)
    SPAWN_COUNT_PER_CONSTELLATION: { 1: 100, 2: 140, 3: 180, 4: 220 } as Record<number, number>,
    SIZE_WEIGHTS: { small: 0.50, medium: 0.30, large: 0.15, huge: 0.05 } as Record<AsteroidSize, number>,
    RESPAWN_TIME_MS: 300_000,        // 5 min
  },
  GAS: {
    // How many gas pockets to spawn inside each nebula
    POCKETS_PER_NEBULA: { 1: 6, 2: 8, 3: 10, 4: 12 } as Record<number, number>,
    SIZE_WEIGHTS: { small: 0.60, medium: 0.30, large: 0.10, huge: 0 } as Record<AsteroidSize, number>,
    RESPAWN_TIME_MS: 240_000,        // 4 min
    // Native nebula resource bias: chance that a gas pocket inherits the nebula's resourceType
    // instead of using constellation-tier weights. Gives each nebula a flavour without new mechanics.
    NATIVE_RESOURCE_BIAS: 0.5,
  },
  WRECKAGE: {
    SPAWN_COUNT_PER_CONSTELLATION: { 1: 8, 2: 12, 3: 16, 4: 20 } as Record<number, number>,
    SIZE_WEIGHTS: { small: 0.40, medium: 0.40, large: 0.20, huge: 0 } as Record<AsteroidSize, number>,
    MIN_DISTANCE_FROM_STATION: 5000, // Lore: abandoned battlefields, far from stations
    RESPAWN_TIME_MS: 600_000,        // 10 min — wreckage is rare
  },
} as const;

export type HarvestableKind = 'asteroid' | 'ice_asteroid' | 'gas_pocket' | 'wreckage';

// ==================== COLLISION CONFIGURATION ====================
// Based on docs/mechanics/collisions.md

export const COLLISION_CONFIG = {
  /** Duration of stun after collision (ms) - inputs ignored during stun */
  STUN_DURATION_MS: 3000,

  /** Bounce coefficient (0 = no bounce, 1 = perfect elastic bounce) */
  RESTITUTION: 0.7,

  /** Minimum impact speed to trigger collision response */
  MIN_IMPACT_SPEED: 10,

  /** Alias for MIN_IMPACT_SPEED (legacy compatibility) */
  MIN_COLLISION_SPEED: 10,

  /** Bounce multiplier when hitting asteroids */
  ASTEROID_BOUNCE_MULTIPLIER: 0.5,

  /** Bounce force multiplier for ship-to-ship collisions */
  BOUNCE_FORCE_MULTIPLIER: 0.4,

  /** Minimum bounce factor for heavy ships (prevents immovable feeling) */
  MIN_BOUNCE_FACTOR: 0.15,

  /** Effective mass cap for gameplay balance (compress 70-600 → 70-300) */
  MASS_COMPRESSION_MAX: 300,

  /** Cooldown between collisions for the same entity pair (ms) */
  COLLISION_COOLDOWN_MS: 500,

  /** Ship collision radius (approximate, ships define their own) */
  SHIP_COLLISION_RADIUS: 25,

  // Asteroid size to collision radius mapping (matches visual radius in AsteroidGraphics)
  ASTEROID_RADII: {
    tiny: 40,
    small: 80,
    medium: 120,
    large: 180,
    huge: 250,
  } as Record<string, number>,

  // Asteroid size to mass mapping (affects physics bounce)
  ASTEROID_MASS: {
    tiny: 100,
    small: 200,
    medium: 400,
    large: 800,
    huge: 1600,
  } as Record<string, number>,
} as const;

/**
 * Calculate asteroid health based on size and resource type.
 * Rarer resources result in tougher asteroids (gold rule: tier-matched mining
 * time stays roughly constant across tiers because miningPower scales with
 * the resource HP multiplier).
 *
 * Note: uses ASTEROID_CONFIG.health as the size baseline, NOT
 * MINING_CONFIG.BASE_HEALTH (which is harvestable-fields-only).
 */
export function getAsteroidHealth(size: AsteroidSize, resourceType: ResourceType): number {
  const baseHealth = ASTEROID_CONFIG[size].health;
  const multiplier = MINING_CONFIG.HEALTH_MULTIPLIERS[resourceType] || 1.0;
  return Math.floor(baseHealth * multiplier);
}

/**
 * Generic health for any harvestable field by size + resource tier (1..4).
 * Mirrors getAsteroidHealth but uses tier instead of a fixed resource enum,
 * so it works for ice asteroids, gas pockets, wreckage.
 */
export const HARVESTABLE_TIER_HEALTH_MULTIPLIERS: Record<number, number> = {
  1: 1.0,
  2: 1.2,
  3: 1.5,
  4: 2.0,
};
export function getHarvestableHealth(size: AsteroidSize, resourceTier: number): number {
  const baseHealth = MINING_CONFIG.BASE_HEALTH[size];
  const multiplier = HARVESTABLE_TIER_HEALTH_MULTIPLIERS[resourceTier] || 1.0;
  return Math.floor(baseHealth * multiplier);
}

/**
 * Calculate the mining zone radius for an asteroid.
 * Players must be within this distance to start mining.
 */
export function getMiningZoneRadius(asteroidRadius: number): number {
  return asteroidRadius * MINING_CONFIG.ZONE_MULTIPLIER + MINING_CONFIG.ZONE_BASE_OFFSET;
}

/**
 * Asymmetric speed multiplier for player mining.
 *
 *   advantage  multiplier   meaning
 *      +3      2.00         T4 tool on T1 resource — farming low-tier with end-game gear
 *      +2      1.67
 *      +1      1.33
 *       0      1.00         tier-matched (the gold rule baseline)
 *      -1      0.40         ~2.5x slower — "you can do it but it's slow"
 *      -2      0.10         ~10x slower — "you're not ready"
 *      -3      0.067        ~15x slower — "effectively gated"
 *
 * Positive side uses a linear formula (1 + adv/3) — compressed from the
 * previous 1 + adv*0.5 so high-tier tools don't trivialise low-tier mining.
 *
 * Negative side is an explicit table, NOT a clean power curve. With the
 * current mP (12/14.4/19.2/24) and HP curve, the absolute T1-tool times on
 * a small asteroid are:
 *   -1 → 9:00 on Copper (9.6 DPS effective)
 *   -2 → 45:00 on Titanium (2.4 DPS effective)
 *   -3 → 89:33 on Platinum (1.6 DPS effective)
 * which doesn't fit pow(x, deficit). Tabulated instead.
 *
 * Sub-1 multipliers depend on fractional damage accumulation in the mining
 * tick loop (see ConstellationRoom.handleMining); the integer floor was previously
 * clamped at 1 dmg/tick which collapsed -2 and -3 to the same effective DPS.
 */
const MINING_PENALTY_BY_DEFICIT: ReadonlyArray<number> = [1.0, 0.4, 0.1, 0.067];
export function computeMiningTierMultiplier(toolTier: number, resourceTier: number): number {
  const advantage = toolTier - resourceTier;
  if (advantage >= 0) return 1 + advantage / 3;
  const deficit = Math.min(-advantage, MINING_PENALTY_BY_DEFICIT.length - 1);
  return MINING_PENALTY_BY_DEFICIT[deficit];
}

/**
 * Get the resource yield range for an asteroid size.
 */
export function getAsteroidResourceYield(size: AsteroidSize): { min: number; max: number } {
  return MINING_CONFIG.RESOURCE_YIELD[size];
}

/**
 * Get the maximum number of miners for an asteroid size.
 */
export function getMaxMiners(size: AsteroidSize): number {
  return MINING_CONFIG.MINING_SLOTS[size];
}

// World configuration
export const WORLD_CONFIG = {
  CONSTELLATION_SIZE: 1800000,       // Regular sector layout doubled October 2026; bodies/speeds unchanged.
  VIEW_RADIUS: 3000,          // Player view radius for interest management (rendering/network)
  ASTEROID_RESPAWN_TIME: 300000,  // 5 minutes in ms
} as const;

// Tutorial constellation (Albion-style tutorial island).
// Permanent constellation with PvP disabled, players routed here on first login, and
// one-way gateways out to each T1 constellation. See docs/mechanics/tutorial.md (TBD).
//
// Physical size is set per-constellation via constellations.size_override in the DB
// (50,000 units → ±25,000 half). All halfSize callsites read from
// GameState.constellationHalfSize, so the small constellation enforces real boundaries.
//
// Layout (50,000 × 50,000, bounds ±25,000):
//   - Academy Hub station at (0, 0), 3,000-radius safe zone
//   - Player spawn ring at radius 1,800–2,800 (inside safe zone)
//   - 4 asteroid zones at the corners (±12k, ±12k), 3,000 radius each
//   - One-way exit gateways at radius 18,000 from center (~7k from edge)
//   - No solar systems, no comets, no nebulae — focused mining ecosystem
export const TUTORIAL_CONFIG = {
  CONSTELLATION_X: 99,                       // Off-grid coords so the constellation doesn't collide with the play grid.
  CONSTELLATION_Y: 99,
  CONSTELLATION_NAME: 'Academy',
  // Full physical size, mirrors constellations.size_override.
  CONSTELLATION_SIZE: 50000,
  // Gateway exit ring: tutorial gateways are placed angularly around the center
  // at this radius so they're easy to find as a fan of "pick your destination".
  EXIT_GATEWAY_RADIUS: 18000,
  // NPC station drone cap (overrides STATION_DRONE_CAPACITY_BY_TIER[1] = 30).
  // 6 = 2 miners / 1 hauler / 3 fighters under STATION_DRONE_COMPOSITION.
  STATION_DRONE_CAPACITY: 6,
  // Aura Beacon spatial position — deliberately offset from the Academy Hub
  // (at origin) so the two entities don't share any influence radius. Hub safe
  // zone is 3000, beacon radius is 750 → centers 8000 apart leaves 4250 of
  // empty space between rings. Small enough that the entire ring fits on
  // screen at default zoom (~0.5x).
  BEACON_X: -8000,
  BEACON_Y: 0,
  BEACON_RADIUS: 750,
  // New-player spawn: random ring around the Aura Beacon, well inside the
  // beacon radius so the tutorial panel triggers on spawn. The Academy Hub is
  // ~8k away — visible from the spawn area but a short flight to reach.
  SPAWN_MIN_RADIUS: 200,
  SPAWN_MAX_RADIUS: 550,
  // Two mining zones per T1 ore (Iron + Copper), placed at the four corners of
  // the central cluster so each is visible from the spawn area but requires a
  // short flight (~12k) to reach. Density matches HOTSPOT_DENSITY → ~56 small
  // asteroids per zone × 4 zones ≈ 220 asteroids total.
  ASTEROID_ZONES: [
    { resource: 'iron',   x:  12000, y:  12000, radius: 3000, density: 0.000002 },
    { resource: 'iron',   x: -12000, y: -12000, radius: 3000, density: 0.000002 },
    { resource: 'copper', x:  12000, y: -12000, radius: 3000, density: 0.000002 },
    { resource: 'copper', x: -12000, y:  12000, radius: 3000, density: 0.000002 },
  ] as ReadonlyArray<{
    resource: 'iron' | 'copper' | 'titanium' | 'platinum';
    x: number; y: number; radius: number; density: number;
  }>,
} as const;

// Tutorial v2 (2026-05): spatial beacon + mission steps.
//
// Modal visibility is client-side gated by proximity to the Aura Beacon entity
// (see TutorialBeaconState). Server stays source of truth for the step (players.
// tutorial_step), but advancement happens manually via Continue, with two steps
// gated by a mission criterion:
//
//   - mission = 'mine'  → server tracks ore mined since the step started;
//                          Continue is rejected until threshold is met.
//   - mission = 'kill'  → server spawns a per-player target drone when the
//                          step begins; Continue is rejected until it dies
//                          by this player's damage.
//   - mission = null    → plain manual Continue, no gate.
//
// Mission completion is checked server-side in the tutorial_advance handler.
// Skip Step (force=true) bypasses the mission gate. Mission state is reset
// when the step is entered (advance OR prev OR skip-step into it).
export type TutorialMission = 'mine' | 'kill';

export const TUTORIAL_MISSION_CONFIG = {
  // Mining mission threshold — total ore (iron + copper combined) the player
  // must add to cargo since the step started. With the post-tuning HP (small
  // Iron 1440 HP, T1 tool 10 DPS = 2:24 to fully mine, avg yield ~4.5):
  // 2 ore ≈ partial-mine ~45% of one small asteroid = ~1 min mining + flight,
  // total tutorial mission ≈ 2-3 min.
  MINE_ORE_THRESHOLD: 2,
} as const;

export const TUTORIAL_STEPS: ReadonlyArray<{
  id: number;
  title: string;
  body: string;
  mission: TutorialMission | null;
  /** For kill missions: which drone type spawns + where. Required when mission='kill'. */
  killSpec?: { droneType: 'miner' | 'fighter'; x: number; y: number };
}> = [
  {
    id: 0,
    mission: null,
    title: 'Welcome to ASTRA',
    body: "I'm Aura — your ship's AI. We're in the Academy, a training constellation. No PvP here, but everything you mine, buy, sell, and kill is real. I'll walk you through the essentials. Tap Continue when you're ready, and fly back to this beacon any time you need a refresher.",
  },
  {
    id: 1,
    mission: null,
    title: 'Move, Boost, Brake',
    body: "WASD thrusts (W/S forward+reverse, A/D strafe). The mouse aims your nose. Hold Left Shift to BOOST — watch the boost gauge. Tap Space to BRAKE hard. Practice when you want; tap Continue when you're ready.",
  },
  {
    id: 2,
    mission: null,
    title: 'Map, Waypoint, Warp',
    body: "Press M to open the constellation map. Left-click any point to drop a WAYPOINT. With a waypoint set, the WARP DRIVE in your HUD becomes READY — click ENGAGE WARP for fast intra-constellation travel. Approach (F) is short-range autopilot; warp is the long-range tool.",
  },
  {
    id: 3,
    mission: null,
    title: 'Mining Basics',
    body: "Asteroid fields surround the Hub — two Iron, two Copper. Fly close, RIGHT-CLICK an asteroid to select it, then press G (Gather) or click MINE in the Overview. You have to be within your mining tool's range. Ore lands in your cargo. Bigger asteroids = more ore, but tougher.",
  },
  {
    id: 4,
    mission: 'mine',
    title: 'Mission: Mine 2 Ore',
    body: "Time to earn your first cargo. Open the map (M), find an asteroid zone, mine 2 ore total (any combination of iron and copper), then fly back here. Continue stays locked until you have the ore in your hold.",
  },
  {
    id: 5,
    mission: null,
    title: 'Sell at the Market',
    body: "Dock at the Academy Hub (press E when close) and open the Market. Sell the ore you mined — instantly to the order book, or post your own listing. Different stations across the universe pay different prices; moving ore IS the economy.",
  },
  {
    id: 6,
    mission: null,
    title: 'The Scanner (Hold R)',
    body: "Hold R to fire your active scan — an expanding sweep around your ship. The LONGER you hold, the bigger the radius. Drones, stations, and entities in range appear in your Overview, on the minimap, and on the constellation map. This is how you spot things that aren't in your radar yet.",
  },
  {
    id: 7,
    mission: null,
    title: 'Combat Primer',
    body: "Select a target (left-click), then APPROACH (F) for one-shot autopilot or FOLLOW (X) to track a moving target. Right-click to fire. Q toggles your SHIELD (blocks damage, costs heat). Weapons + shield generate HEAT — overheat and they lock. V starts a VENT (2× cooling, weapons locked until heat = 0).",
  },
  {
    id: 8,
    mission: 'kill',
    killSpec: { droneType: 'miner', x: 7000, y: 4000 },
    title: 'Mission: Hunt a Rogue Miner',
    body: "I've flagged a rogue miner drone for you. Last sighting puts it near coordinates (7000, 4000) — northeast of the Hub. Drop a waypoint there on the constellation map (M), warp or fly in, then hold R to sweep the area with your scanner until it appears in your Overview. Right-click to select, close to weapon range, and put it down. This one won't fight back. The target is yours alone — no other pilot can see it or shoot it.",
  },
  {
    id: 9,
    mission: 'kill',
    killSpec: { droneType: 'fighter', x: -7000, y: -4000 },
    title: 'Mission: Hunt a Rogue Fighter',
    body: "Real combat now. A rogue fighter drone has been sighted near coordinates (-7000, -4000) — southwest of the Hub. This one IS armed and will shoot back. Raise your SHIELD (Q) before you close in, use APPROACH (F) to engage, and right-click to fire. If your weapons lock from heat, hit V to VENT. Don't be a hero — back off and reset if your hull drops. Like before, the target is yours alone.",
  },
  {
    id: 10,
    mission: null,
    title: 'Choose Your Path',
    body: "Two gateways exit the Academy — one to each T1 constellation currently online. Once you jump, you can't come back. Pick a destination, take what you've learned, and good wondering out there, pilot.",
  },
];


// Game clock — purely cosmetic, 1:1 with real time (like EVE Online).
// Real calendar, just offset the year by +998 so 2024 → 3022, 2026 → 3024, etc.
export const GAME_CLOCK = {
  YEAR_OFFSET: 998,                    // real year + offset = game year (2026 → 3024)
  ERA_SUFFIX: 'A.E.',                  // "After Earth"
} as const;

// Network configuration
export const NETWORK_CONFIG = {
  TICK_RATE: 20,              // Updates per second
  POSITION_SAVE_INTERVAL: 30000,  // Save position every 30 seconds
} as const;

// Anti-cheat: bounded aggregate travel audit for client-authoritative movement.
// Credit refills from server time at maxSpeed * TOLERANCE, and each accepted
// displacement spends it. Same-ms delivery bursts share the stored credit.
// TOLERANCE covers the existing 1.5x boost ceiling plus timing/position precision;
// MAX_DT_FOR_CHECK_MS bounds stored jitter credit, including after an idle gap.
// Neither packet count, client timestamps nor rejection can refill the budget.
export const MOVEMENT_VALIDATION = {
  TOLERANCE: 1.8,
  MAX_DT_FOR_CHECK_MS: 500,
} as const;

// Anti-cheat: server-side beam-damage validation for energy/laser weapons.
// The client used to compute ramp-up locally and send a damage number; the
// server now derives damage from the equipped weapon's stats and a per-
// attacker beam tracker. Constants picked so well-behaved clients see
// damage close to their prediction; cheaters can't spam messages to
// inflate damage because dt-per-tick is capped server-side.
//
//   BEAM_STALE_MS → if the attacker hasn't landed a beam tick on this target
//                    in this long, the next message is treated as a new beam
//                    (ramp resets to 1.0x).
//   MAX_TICK_DT_SEC → caps how much beam time one server-applied tick can
//                      represent, regardless of message timing. Prevents
//                      "spam messages, claim huge dt" exploits.
//   FIRST_TICK_SEC → on a fresh beam (target switch or stale), the first
//                     tick applies this much beam time. Matches the client's
//                     batched send interval so legit play feels identical.
export const BEAM_VALIDATION = {
  BEAM_STALE_MS: 500,
  MAX_TICK_DT_SEC: 0.2,
  FIRST_TICK_SEC: 0.1,
} as const;

// Anti-cheat: server-side fire-rate enforcement for projectile weapons
// (kinetic, explosive). Beam weapons skip this — their server-side dt cap
// in BEAM_VALIDATION already bounds damage per unit time.
//
//   TOLERANCE → fraction of the weapon's expected gap (1000/fireRate ms)
//                that the server requires between accepted attacks. 0.9 leaves
//                ~10% jitter slack so legit clients aren't dropped under
//                normal network conditions.
export const FIRE_RATE_VALIDATION = {
  TOLERANCE: 0.9,
} as const;

// WebSocket disconnect codes (4000-4999 are application-specific)
export const DISCONNECT_CODES = {
  DUPLICATE_SESSION: 4001,    // Logged in from another browser/device
} as const;

// Drop configuration
export const DROP_CONFIG = {
  LIFESPAN_MS: 3_600_000,         // 1 hour (drops are persistent entities in the world)
  INTERACTION_RANGE: 250,         // Distance at which the pickup prompt appears
  OWNERSHIP_DURATION_MS: 60_000,  // 60s killer-only protection window
  MONEY_DROP_PERCENT_MIN: 0.1,    // 10% minimum
  MONEY_DROP_PERCENT_MAX: 0.5,    // 50% maximum
  EQUIPMENT_DROP_CHANCE: 0.3,     // 30% per equipped item
  // Legacy alias used by some older callers (kept for backwards compat)
  PICKUP_RADIUS: 250,
} as const;

// Station zone radii
export const STATION_ZONES = {
  SAFE_ZONE_RADIUS: 2000,   // Outer ring - no weapons/damage
  DOCKING_RADIUS: 1000,      // Inner ring - can dock
} as const;

// Planet zone configuration
// Landing range is relative to planet radius - must be within 1.5x and outside 0.8x
export const PLANET_ZONES = {
  LANDING_RANGE_MULTIPLIER: 1.5,   // Can land when distance < radius * 1.5
  SURFACE_MULTIPLIER: 0.8,         // Cannot land if too close (inside surface)
} as const;

// ==================== GATEWAY CONFIGURATION ====================

export const GATEWAY_CONFIG = {
  ACTIVATION_RADIUS: 500,       // Distance to interact with gateway
  VISUAL_RADIUS: 150,           // Size of gateway visual
  SPAWN_DISTANCE: 100,          // How far from gate player spawns in destination
  COOLDOWN_MS: 10000,           // 10 seconds before can use another gateway
  TRANSITION_DURATION_MS: 2000, // 2 seconds for transition cutscene
  EDGE_OFFSET: 18000,           // Layout margin; gateway interaction radii stay unchanged.
  CLOAK_DURATION_MS: 20000,     // 20 seconds of invisibility after gateway travel
} as const;

// ==================== HARD FLUX (lite) ====================
// Single-pool model: absorbedHeat is a counter tracking how much of currentHeat
// originated from shield-absorbed damage. While shield is RAISED, that portion
// does NOT dissipate. Lowering the shield "thaws" it (absorbedHeat → 0) and
// it dissipates normally. Vent locks weapons until heat drops below threshold.
//
// Heat from your OWN weapons + shield upkeep keeps dissipating normally even
// while shielded — so you can block + fire simultaneously, but only the
// absorbed heat is the "cost" of blocking. This is option B (Starsector-aligned).
//
// VARIANT A (not implemented, kept here as a design note): if you wanted
// blocking to also suspend dissipation of weapon heat, you'd set
// SUSPEND_ALL_DISSIPATION_WHILE_SHIELDED to true. That's the "brutal" version
// where firing + blocking compounds heat from both sides. Left at false.
export const HARD_FLUX_CONFIG = {
  /** Master toggle. False = legacy behavior (all heat dissipates always). */
  ENABLED: true,

  /**
   * Fraction of normal dissipation rate applied to the absorbed-heat portion
   * while shield is raised. 0 = fully frozen (Starsector strict). 0.15 (default)
   * = small drain so a sustained blocker accumulates less, making the
   * eventual vent shorter and more survivable. Tune up if blocking still feels
   * too punishing; tune down to make heat management tighter.
   */
  ABSORBED_HEAT_DISSIPATION_MULT_WHILE_SHIELDED: 0.15,

  /**
   * VARIANT A toggle (the brutal one). If true, suspends ALL dissipation
   * while shield is up — not just absorbed heat. Default false.
   */
  SUSPEND_ALL_DISSIPATION_WHILE_SHIELDED: false,

  /**
   * Vent lock: while `isVenting` is true, weapons (including beams) cannot fire.
   * Starsector default = false (you can't fire during an active vent).
   * Set to true to test "shoot while venting" variant — but then vent needs
   * another cost (not implemented; only this toggle).
   */
  CAN_FIRE_WHILE_VENTING: false,

  /**
   * While isVenting, can the player re-raise the shield to abort the vent?
   * Starsector default = false (committed action — you finish the vent or
   * you don't get to bail). Set true to make vents cancellable.
   */
  CAN_RAISE_SHIELD_WHILE_VENTING: false,

  /**
   * Voluntary vent dissipation multiplier — Starsector default 2.0 (twice as
   * fast as passive shield-down dissipation). The asymmetry vs overload
   * (overload disipates at 1x and you can't escape it) is what rewards good
   * heat management.
   */
  VOLUNTARY_VENT_DISSIPATION_MULT: 2.0,

  /**
   * isVenting releases when currentHeat drops below maxHeat × this fraction.
   * 0 = full commitment, vent runs to absolute zero. Raise slightly (e.g. 0.05)
   * if you want vents to feel snappier at the cost of carrying residual heat.
   */
  VENT_RELEASE_THRESHOLD_FRACTION: 0.0,
} as const;

// Ship regeneration configuration
export const REGEN_CONFIG = {
  // Hull regeneration (slow passive repair)
  HULL_REGEN_RATE: 2,               // HP per second
  HULL_REGEN_DELAY: 8000,           // ms before regen starts after taking damage
  HULL_REGEN_PERCENT_CAP: 0.75,     // Only regen up to 75% of max hull (need repairs for full)

  // Shield regeneration (uses equipment stats if available, otherwise defaults)
  SHIELD_REGEN_RATE_DEFAULT: 5,     // HP per second if no shield equipped
  SHIELD_REGEN_DELAY_DEFAULT: 3000, // ms before regen starts if no shield equipped

  // Regeneration is paused during combat
  COMBAT_TIMEOUT: 5000,             // ms of no damage before considered out of combat

  // Safe zone bonus - regeneration is faster when inside station safe zones
  SAFE_ZONE_REGEN_MULTIPLIER: 3.0,  // 3x faster regen in safe zone
  SAFE_ZONE_HULL_CAP: 1.0,          // Full hull regen allowed in safe zone
  SAFE_ZONE_DELAY_MULTIPLIER: 0.5,  // Half the delay in safe zone
} as const;

// ==================== ECONOMY CONFIGURATION ====================

// Marketplace configuration (per game_design.md - 3% transaction fee, 1% listing fee)
export const MARKETPLACE_CONFIG = {
  TRANSACTION_FEE_RATE: 0.03,       // 3% per trade (sink)
  LISTING_FEE_RATE: 0.01,           // 1% of order value (prevents spam)
  MIN_PRICE: 1,                     // Minimum price per unit
  MAX_PRICE: 1_000_000_000,         // Maximum price per unit (1 billion)
  MAX_QUANTITY_PER_ORDER: 10_000,   // Maximum items per order
  ORDER_EXPIRY_DAYS: 30,            // Orders expire after 30 days
  MAX_ACTIVE_ORDERS_PER_PLAYER: 50, // Limit active orders per player
} as const;

// Stolen / dirty items (docs/stolen items.md). The laundering rate is THE balance lever
// and is LIVE-TUNABLE — its real value lives in the `game_config` table (key below) and is
// read cached at runtime. This constant is only the fallback when that row is unreadable.
export const DIRTY_ITEMS_CONFIG = {
  CONFIG_KEY_LAUNDERING_RATE: 'laundering_rate',
  DEFAULT_LAUNDERING_RATE: 0.30,    // fallback only; tune live via game_config (docs §5)
} as const;

// ==================== MARKET MAKER CONFIGURATION ====================
// Virtual liquidity provider with dynamic spreads and daily limits
// See game_design.md section 3 - Market Maker Bots (Fixed the Exploit)

export const MARKET_MAKER_CONFIG = {
  // Activity level thresholds (trades per day at this station for this item)
  LOW_ACTIVITY_THRESHOLD: 10,      // < 10 trades/day = low activity
  MEDIUM_ACTIVITY_THRESHOLD: 50,   // 10-50 trades/day = medium activity
                                    // > 50 trades/day = high activity

  // Spread percentages by activity level
  // Spread determines MM buy/sell prices relative to base price
  // Higher spread = worse prices for players = less exploitable
  LOW_SPREAD: 0.30,      // 30% spread for low activity
  MEDIUM_SPREAD: 0.15,   // 15% spread for medium activity
  HIGH_SPREAD: 0.05,     // 5% spread for high activity

  // Daily volume limits per item (prevents infinite arbitrage exploit)
  LOW_DAILY_LIMIT: 500,     // 500 units/day in low activity markets
  MEDIUM_DAILY_LIMIT: 200,  // 200 units/day in medium activity markets
  HIGH_DAILY_LIMIT: 100,    // 100 units/day in high activity markets
} as const;

// Base prices for ALL tradeable resources
// Used by Market Maker for pricing when no player orders exist
// Prices from RESOURCES (constants.ts), ICE_RESOURCES, GAS_RESOURCES, REFINED_RESOURCES (resources.ts)
export const RESOURCE_BASE_PRICES: Record<string, number> = {
  // Ore resources (from RESOURCES constant above)
  iron: 10,
  copper: 25,
  titanium: 75,
  platinum: 200,

  // Ice resources
  water_ice: 12,
  ammonia: 30,
  methane: 80,
  helium3: 250,

  // Gas resources
  hydrogen: 8,
  nitrogen: 20,
  xenon: 60,
  antimatter: 200,

  // Refined resources
  steel_alloy: 35,
  cryo_bronze: 85,
  carbide_plate: 250,
  quantum_alloy: 700,
} as const;

export type MMActivityLevel = 'low' | 'medium' | 'high';

// ==================== WORLD OBJECT CONFIGURATION ====================

// Sun/Star configuration
// Suns are environmental hazards only - NOT harvestable
// Damage increases exponentially toward center - instantly lethal at core
export const SUN_CONFIG = {
  TYPES: {
    red_dwarf: {
      radius: 600,
      damageRadius: 900,           // 1.5x visual radius
      baseDamagePerSecond: 2,
      color: 0xff4422,
    },
    yellow: {
      radius: 900,
      damageRadius: 1350,          // 1.5x visual radius
      baseDamagePerSecond: 3,
      color: 0xffdd44,
    },
    blue_giant: {
      radius: 1350,
      damageRadius: 2000,          // ~1.5x visual radius
      baseDamagePerSecond: 5,
      color: 0x4488ff,
    },
    neutron: {
      radius: 300,
      damageRadius: 600,           // 2x visual radius (neutron still deadlier)
      baseDamagePerSecond: 8,
      color: 0xcc44ff,
    },
  },
  // Exponential damage curve: damage = baseDPS * e^(k * proximity)
  // At edge (proximity=0): damage = baseDPS
  // At center (proximity=1): damage = baseDPS * e^4 ≈ baseDPS * 55
  DAMAGE_EXPONENT: 4,              // Gentler ramp - flyby survivable
  CENTER_DAMAGE_MULTIPLIER: 500,   // Lethal at center but not absurd
} as const;

export type StarType = keyof typeof SUN_CONFIG.TYPES;

// Nebula configuration
export const NEBULA_CONFIG = {
  TYPES: {
    emission: {
      resourceType: 'hydrogen' as const,
      density: 0.6,
      colorR: 255, colorG: 100, colorB: 100,  // Reddish
    },
    reflection: {
      resourceType: 'nitrogen' as const,
      density: 0.5,
      colorR: 100, colorG: 150, colorB: 255,  // Bluish
    },
    planetary: {
      resourceType: 'xenon' as const,
      density: 0.4,
      colorR: 150, colorG: 100, colorB: 200,  // Purple
    },
    supernova: {
      resourceType: 'antimatter' as const,
      density: 0.3,
      colorR: 255, colorG: 50, colorB: 255,   // Bright magenta
    },
  },
  MIN_RADIUS: 1500,
  MAX_RADIUS: 3000,
  COLLECTION_RATE_PER_SECOND: 0.3, // Base resources per second
  COLLECTION_TICK_MS: 1000,        // How often collection ticks occur
} as const;

export type NebulaType = keyof typeof NEBULA_CONFIG.TYPES;

// ==================== ENTITY BOUNDS CONFIGURATION ====================

// Entity collision radii for boundary enforcement
export const ENTITY_RADII = {
  PLAYER_SHIP: 50,      // Approximate ship radius for boundary collision
  BOT_SHIP: 50,         // Same for bots
  ASTEROID_SMALL: 80,
  ASTEROID_MEDIUM: 120,
  ASTEROID_LARGE: 180,
  ASTEROID_HUGE: 250,
} as const;

/**
 * Clamp a position to constellation bounds, accounting for entity radius.
 * Each ConstellationRoom uses LOCAL coordinates centered at (0,0); constellationX/constellationY
 * are accepted but ignored to preserve the existing signature.
 *
 * `constellationSize` is the FULL size of the constellation (mirrors WORLD_CONFIG.CONSTELLATION_SIZE).
 * Pass the per-constellation override when known; defaults to the global constant for
 * legacy callers that haven't been migrated yet.
 */
export function clampToConstellationBounds(
  x: number,
  y: number,
  _constellationX: number,
  _constellationY: number,
  entityRadius: number = 0,
  constellationSize: number = WORLD_CONFIG.CONSTELLATION_SIZE,
): { x: number; y: number; clampedX: boolean; clampedY: boolean } {
  const halfSize = constellationSize / 2;

  const minX = -halfSize + entityRadius;
  const maxX = halfSize - entityRadius;
  const minY = -halfSize + entityRadius;
  const maxY = halfSize - entityRadius;

  const clampedX = x < minX || x > maxX;
  const clampedY = y < minY || y > maxY;

  return {
    x: Math.max(minX, Math.min(maxX, x)),
    y: Math.max(minY, Math.min(maxY, y)),
    clampedX,
    clampedY,
  };
}

/**
 * Check if a position is within constellation bounds (with optional radius buffer).
 * Each ConstellationRoom uses LOCAL coordinates centered at (0,0); constellationX/constellationY
 * are accepted but ignored to preserve the existing signature.
 *
 * See clampToConstellationBounds for the constellationSize parameter semantics.
 */
export function isWithinConstellationBounds(
  x: number,
  y: number,
  _constellationX: number,
  _constellationY: number,
  entityRadius: number = 0,
  constellationSize: number = WORLD_CONFIG.CONSTELLATION_SIZE,
): boolean {
  const halfSize = constellationSize / 2;

  const minX = -halfSize + entityRadius;
  const maxX = halfSize - entityRadius;
  const minY = -halfSize + entityRadius;
  const maxY = halfSize - entityRadius;

  return x >= minX && x <= maxX && y >= minY && y <= maxY;
}

// ==================== THREAT LEVEL CONFIGURATION ====================

export const THREAT_LEVELS = ['green', 'yellow', 'red'] as const;
export type ThreatLevel = typeof THREAT_LEVELS[number];

export const THREAT_CONFIG = {
  // Kill count thresholds (kills per hour)
  GREEN_MAX_KILLS: 2,      // 0-2 kills/hr = Green
  YELLOW_MAX_KILLS: 5,     // 3-5 kills/hr = Yellow
  // 6+ kills/hr = Red

  // Time window for counting kills (1 hour in ms)
  KILL_WINDOW_MS: 60 * 60 * 1000,

  // How often to recalculate threat level (60 seconds)
  RECALCULATION_INTERVAL_MS: 60 * 1000,

  // Constellation tiers capped at Yellow (cannot go Red)
  CAPPED_TIERS: [1, 2] as readonly number[],

  // Maximum threat level for capped tiers
  CAPPED_MAX_THREAT: 'yellow' as ThreatLevel,
} as const;

// ==================== DEATH PENALTY CONFIGURATION ====================

export const DEATH_PENALTY_CONFIG = {
  // Credit drop percentages by threat level (these become lootable drops)
  CREDIT_LOSS: {
    green: 0.05,    // 5% credits dropped
    yellow: 0.10,   // 10% credits dropped
    red: 0.15,      // 15% credits dropped
  } as Record<ThreatLevel, number>,

  // Equipment drop chance per item by threat level
  // Green zones are SAFE - no equipment loss
  EQUIPMENT_DROP_CHANCE: {
    green: 0,       // NO equipment drop in green zones
    yellow: 0.20,   // 20% per item in yellow
    red: 0.35,      // 35% per item in red
  } as Record<ThreatLevel, number>,

  // Ship loss by threat level
  // Red zones = ship is PERMANENTLY DELETED
  SHIP_LOST: {
    green: false,   // Ship is kept
    yellow: false,  // Ship is kept
    red: true,      // Ship is PERMANENTLY DELETED
  } as Record<ThreatLevel, boolean>,

  // The threat level at which ships are permanently lost
  SHIP_LOSS_THREAT_LEVEL: 'red' as ThreatLevel,
} as const;

// ==================== WARP TRAVEL CONFIGURATION ====================

// Warp phases
export const WARP_PHASES = ['none', 'ready', 'calculating', 'traveling', 'cooldown'] as const;
export type WarpPhase = typeof WARP_PHASES[number];

// Warp interruption reasons
export const WARP_INTERRUPT_REASONS = [
  'damage',
  'disruptor',
  'beacon',
  'manual',
  'arrival',
  'boundary',
  'radiation',  // Emergency exit when approaching sun
  'nebula',     // Nebula interference disrupts warp
] as const;
export type WarpInterruptReason = typeof WARP_INTERRUPT_REASONS[number];

export const WARP_CONFIG = {
  // Preparation time after setting waypoint
  SUSTAINED_MOVEMENT_TIME_MS: 3000,           // 3 seconds to prepare warp

  // Calculation phase duration by ship size
  CALCULATION_TIME_MS: {
    small: 3000,   // Fast ships: 3 seconds
    medium: 5000,  // Medium ships: 5 seconds
    large: 10000,  // Slow ships: 10 seconds
  } as Record<ShipSize, number>,

  // Warp speed multipliers by ship class type
  WARP_SPEED_MULTIPLIER: {
    corvette: 22,      // Fastest: ~4500 u/s
    frigate: 20,       // Fast: ~3900 u/s
    destroyer: 17,     // Medium-fast: ~3300 u/s
    cruiser: 15,       // Medium: ~3000 u/s
    carrier: 14,       // Medium-slow: ~2700 u/s
    battleship: 12,    // Slow: ~2400 u/s
    dreadnought: 11,   // Slowest: ~2100 u/s
    hauler: 12,        // Slow: ~2400 u/s
  } as Record<ShipClassType, number>,

  // Post-warp cooldown
  COOLDOWN_MS: 60000,  // 60 seconds

  // Arrival distance (stop warp when this close to target)
  ARRIVAL_DISTANCE: 200,

  // Visual config
  WARP_TRAIL_LENGTH: 100,
  WARP_COLOR: 0x00aaff,
} as const;

// ==================== INTERDICTION BEACON CONFIGURATION ====================

export const INTERDICTION_BEACON_CONFIG = {
  // Beacon properties by tier
  TIERS: {
    1: { radius: 500, durationMs: 60000, health: 100, cost: 1000 },
    2: { radius: 650, durationMs: 90000, health: 150, cost: 3000 },
    3: { radius: 800, durationMs: 120000, health: 200, cost: 8000 },
    4: { radius: 1000, durationMs: 180000, health: 300, cost: 20000 },
  } as Record<number, { radius: number; durationMs: number; health: number; cost: number }>,

  // Maximum beacons per player
  MAX_BEACONS_PER_PLAYER: 3,

  // Minimum distance from stations (no trapping in safe zones)
  MIN_DISTANCE_FROM_STATION: 2000,

  // Deploy cooldown
  DEPLOY_COOLDOWN_MS: 30000,  // 30 seconds between deployments
} as const;

// ==================== NEW PLAYER PROTECTION CONFIGURATION ====================
// See game_design.md Section 10 - Power-Based Protection
// Ships below 50 power cannot be attacked by ships above 150 power

export const PROTECTION_CONFIG = {
  /** Ships below this power level are considered "protected" (new players) */
  PROTECTED_POWER_THRESHOLD: 50,

  /** Ships above this power level cannot attack protected players */
  ATTACKER_POWER_THRESHOLD: 150,

  /** Duration of aggressor flag after protected player attacks (ms) */
  AGGRESSOR_TIMEOUT_MS: 60000,  // 1 minute
} as const;

// ==================== NEW PLAYER STARTING CONDITIONS ====================
// See game_design.md Section 10 - Starting Conditions

export const NEW_PLAYER_CONFIG = {
  /** Starting credits for new players */
  STARTING_CREDITS: 500,

  /** Ship class ID for starter ship */
  STARTING_SHIP: 'corvette_t1',

  /** Starting constellation coordinates (T1 constellation) */
  STARTING_CONSTELLATION_X: 0,
  STARTING_CONSTELLATION_Y: 0,
} as const;

// ==================== REPUTATION CONFIGURATION ====================
// Player reputation system: tracks good/bad behavior
// Range: -1000 (Outlaw) to +1000 (Hero), starting at 0 (Neutral)
// See docs/mechanics/reputation.md for full documentation

export const REPUTATION_CONFIG = {
  // Reputation bounds
  MIN_REPUTATION: -1000,
  MAX_REPUTATION: 1000,
  NEUTRAL: 0,

  // Thresholds for status effects
  WANTED_THRESHOLD: -500,     // Police start attacking
  OUTLAW_THRESHOLD: -750,     // Police call backup, maximum hostility
  HERO_THRESHOLD: 500,        // Station repair discounts

  // Kill penalties (subtract from killer's reputation)
  KILL_PENALTY_T1_T2: -500,   // Killing in T1/T2 constellations (newbie zones)
  KILL_PENALTY_T3_T4: -100,   // Killing in T3/T4 constellations (lawless space)

  // Weak target penalty: additional penalty if power difference > threshold
  WEAK_TARGET_POWER_THRESHOLD: 100,
  WEAK_TARGET_PENALTY_MIN: -100,
  WEAK_TARGET_PENALTY_MAX: -200,

  // Kill rewards (for killing wanted/outlaw players)
  // Reward = bounty_multiplier * abs(victim_reputation)
  KILL_WANTED_BOUNTY_MULTIPLIER: 0.10,    // 10% of victim's |rep| as reward
  KILL_OUTLAW_BOUNTY_MULTIPLIER: 0.20,    // 20% of victim's |rep| as reward

  // Decay: reputation moves toward 0 over time (only negative rep decays)
  DECAY_RATE_PER_HOUR: 10,    // Points per hour toward 0
  DECAY_INTERVAL_MS: 60000,   // Check every minute (1 minute)

  // Hero benefits
  HERO_REPAIR_DISCOUNT: 0.10, // 10% repair cost discount
} as const;

// Reputation status type
export type ReputationStatus = 'outlaw' | 'wanted' | 'neutral' | 'hero';

// ==================== BOUNTY SYSTEM CONFIGURATION ====================
// Bounty system for tracking and rewarding criminal hunting
// See docs/sessions/bounty_plan.md for full documentation

export const BOUNTY_CONFIG = {
  /** Percentage of dropped credits that go to bounty pool */
  CREDIT_TO_BOUNTY_RATIO: 0.80,

  /** Maximum credits system adds to small bounties */
  SYSTEM_TOP_UP_MAX: 500,

  /** How often bounty list is broadcast to all clients (ms) */
  BROADCAST_INTERVAL_MS: 300000, // 5 minutes

  /** Minimum bounty to include in broadcast */
  MIN_BOUNTY_TO_BROADCAST: 100,

  /** Reputation gain multiplier when claiming bounty (10% of bounty value) */
  BOUNTY_REP_MULTIPLIER: 0.10,
} as const;

/** Bounty information for a wanted player */
export interface BountyInfo {
  playerId: string;
  playerName: string;
  amount: number;
  lastConstellationId: string;
  lastConstellationName: string;
  lastShipClass: string;
  lastShipTier: number;
  updatedAt: number; // timestamp
}

